/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description ResubmitEligibilityRequest
 */
// TODO action button class

import { Button as AntButton, App, Tooltip, ButtonProps } from 'antd';
import { Redo } from '@icon-park/react';

import { MODAL_TYPES } from '../modal';
import usePostEligibilityRequest from 'hooks/eligibility/mutate/usePostEligibilityRequest';
import { getPayerMeta } from 'utils/eligibility/eligibilityResponse';
import { prepareEligibilityResubmitModel } from 'utils/eligibility/eligibilityRequestForm';
import EventTracker from 'utils/eventTracker';
import { EVENTS } from 'app-constants/events';

/**
 * interface for props
 */
export interface IProps {
	data: EligibilityResponse.IModel | EligibilityRequests.IEligibilityData;
	payer?: Payers.ISelectedPayer;
	providerMeta?: Metadata.IProviderMeta;
	isValidLocation: boolean;
	label?: React.ReactNode;
}

const getModalTitle = (isArchivedPayer: boolean, isInValidNpi: boolean, isValidLocation: boolean): string => {
	if (isArchivedPayer && isInValidNpi) {
		return "Can't resubmit this request anymore as this payer is archived and the NPI is disabled or has not completed eKYC verification yet.";
	} else if (isArchivedPayer) {
		return "Can't resubmit this request anymore as this payer is archived.";
	} else if (!isValidLocation) {
		return "Can't resubmit this request anymore as this location is disabled.";
	} else {
		return "Can't resubmit this request anymore as this NPI is disabled or has not completed eKYC verification yet.";
	}
};

/**
 * ResubmitEligibilityRequest
 *
 */
const ResubmitEligibilityRequest: React.FC<IProps & ButtonProps> = ({
	data,
	payer,
	providerMeta,
	isValidLocation,
	label,
	...restProps
}) => {
	const { modal } = App.useApp();
	const { mutateAsync } = usePostEligibilityRequest();
	const isArchivedPayer = getPayerMeta(payer).isArchived;
	const isInValidNpi = !providerMeta?.canMakeRequests;

	const confirmSubmit = () => {
		if (isArchivedPayer || isInValidNpi || !isValidLocation) {
			modal.error({
				...MODAL_TYPES.warning,
				width: 450,
				content: getModalTitle(isArchivedPayer, isInValidNpi, isValidLocation),
			});
		} else {
			modal.confirm({
				...MODAL_TYPES.confirm,
				content: 'Are you sure you want to resubmit the request?',
				okText: 'Resubmit',
				onOk: () => {
					EventTracker.trackEvent(EVENTS.eligibilityRequest.submit('resubmit'));
					return mutateAsync({ model: prepareEligibilityResubmitModel(data) });
				},
			});
		}
	};

	if (label) {
		return (
			<AntButton onClick={confirmSubmit} {...restProps}>
				{label}
			</AntButton>
		);
	}

	return (
		<Tooltip title='Resubmit Request'>
			<AntButton color='default' onClick={confirmSubmit} icon={<Redo />} {...restProps} />
		</Tooltip>
	);
};

export default ResubmitEligibilityRequest;
